import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { ComputedResult } from "@/components/_pages/ModelPage/Sidebar/ComputedResult";
import { InputDollar } from "@/components/_pages/ModelPage/Sidebar/InputDollar";
import { InputPercent } from "@/components/_pages/ModelPage/Sidebar/InputPercent";
import { InputToggle } from "@/components/_pages/ModelPage/Sidebar/InputToggle";
import { InputYears } from "@/components/_pages/ModelPage/Sidebar/InputYears";
import { sbaLoanAmortizationScheduleAtom } from "@/financeModels/owasco/amortization";
import {
  isConventionalLoanTypeAtom,
  sbaLoanConventionalAmortScheduleAtom,
  sbaLoanConventionalHasInterestOnlyPeriodAtom,
  sbaLoanConventionalInterestOnlyPeriodAtom,
  sbaLoanConventionalTermLengthAtom,
  sbaLoanConventionalTransactionFeeAtom,
  sbaLoanErrorMessageAtom,
  sbaLoanInterestRateAtom,
  sbaLoanIsInErrorAtom,
} from "@/financeModels/owasco/general";
import { numberToPercentInput, percentInputToNumber } from "@/utils/format";
import { lazyReactFallback } from "@/utils/lazy";
import { toNumberOrNull } from "@/utils/math";
import { Alert, Stack } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import { ChangeEvent } from "react";
import { TbBuildingBank } from "react-icons/tb";
import { DealFees } from "./DealFees";

const Sba7aLoanInterestRateExplainer = lazyReactFallback(
  () => import("@/docs/pages/model/sidebar/sba7aLoanInterestRate.mdx"),
);
const Sba7aLoanAmortScheduleExplainer = lazyReactFallback(
  () => import("@/docs/pages/model/sidebar/sba7aLoanAmortSchedule.mdx"),
);

// const Sba7aLoanLoanStartDateExplainer = lazyReactFallback(
//   () => import("@/docs/pages/model/sidebar/sba7aLoanLoanStartDate.mdx"),
// );

const Panel = () => {
  const [interestRate, setInterestRate] = useAtom(sbaLoanInterestRateAtom);
  //const [loanStartDate, setLoanStartDate] = useAtom(sbaLoanStartDateAtom);
  const amortSchedule = useAtomValue(sbaLoanAmortizationScheduleAtom);
  const isConventionalLoanType = useAtomValue(isConventionalLoanTypeAtom);

  const [sbaLoanConventionalTermLength, setConventionalTermLength] = useAtom(
    sbaLoanConventionalTermLengthAtom,
  );
  const [sbaLoanConventionalAmortSchedule, setConventionalAmortSchedule] =
    useAtom(sbaLoanConventionalAmortScheduleAtom);

  const [sba7aLoanConventionalHasInterestOnlyPeriod, setHasInterestOnlyPeriod] =
    useAtom(sbaLoanConventionalHasInterestOnlyPeriodAtom);

  const [
    sbaLoanConventionalInterestOnlyPeriod,
    setConventionalInterestOnlyPeriod,
  ] = useAtom(sbaLoanConventionalInterestOnlyPeriodAtom);

  const [sbaLoanConventionalTransactionFee, setConventionalTransactionFee] =
    useAtom(sbaLoanConventionalTransactionFeeAtom);

  const errorMessage = useAtomValue(sbaLoanErrorMessageAtom);

  return (
    <Stack gap={4}>
      <InputPercent
        label={
          <LabelExplainer
            label="Interest Rate"
            explanation={<Sba7aLoanInterestRateExplainer />}
          />
        }
        value={numberToPercentInput(interestRate)}
        onChange={(value) => setInterestRate(percentInputToNumber(value))}
      />
      {!isConventionalLoanType && (
        <ComputedResult
          label={
            <LabelExplainer
              label="Amortization Schedule"
              explanation={<Sba7aLoanAmortScheduleExplainer />}
            />
          }
          value={amortSchedule.toFixed(2).replace(".00", "") + " years"}
          variant="default"
          my={6}
        />
      )}
      {isConventionalLoanType && (
        <>
          <InputYears
            value={toNumberOrNull(sbaLoanConventionalTermLength) || ""}
            onChange={(value) =>
              setConventionalTermLength(toNumberOrNull(value))
            }
            label={
              <LabelExplainer
                label="Term Length"
                explanation={
                  <Doc path="@/docs/pages/model/sidebar/sba7aLoanConventionalTermLength.mdx" />
                }
              />
            }
          />
          <InputYears
            value={toNumberOrNull(sbaLoanConventionalAmortSchedule) || ""}
            onChange={(value) =>
              setConventionalAmortSchedule(toNumberOrNull(value))
            }
            label={
              <LabelExplainer
                label="Amortization Schedule"
                explanation={
                  <Doc path="@/docs/pages/model/sidebar/sba7aLoanConventionalAmortSchedule.mdx" />
                }
              />
            }
          />

          <InputToggle
            label={
              <LabelExplainer
                label="Has Interest-Only Period ?"
                explanation={
                  <Doc path="@/docs/pages/model/sidebar/sba7aLoanConventionalHasInterestOnlyPeriod.mdx" />
                }
              />
            }
            checked={sba7aLoanConventionalHasInterestOnlyPeriod}
            onChange={(event) =>
              setHasInterestOnlyPeriod(
                (event as ChangeEvent<HTMLInputElement>).currentTarget.checked,
              )
            }
          />
          {sba7aLoanConventionalHasInterestOnlyPeriod && (
            <InputYears
              rightSection="months"
              rightSectionWidth={80}
              value={
                toNumberOrNull(sbaLoanConventionalInterestOnlyPeriod) || ""
              }
              onChange={(value) =>
                setConventionalInterestOnlyPeriod(toNumberOrNull(value))
              }
              label={
                <LabelExplainer
                  label="Interest-Only Period"
                  explanation={
                    <Doc path="@/docs/pages/model/sidebar/sba7aLoanConventionalInterestOnlyPeriod.mdx" />
                  }
                />
              }
            />
          )}

          <InputDollar
            value={Number(sbaLoanConventionalTransactionFee)}
            onChange={(value) => setConventionalTransactionFee(value)}
            label={
              <LabelExplainer
                label="Loan Transaction Fee"
                explanation={
                  <Doc path="@/docs/pages/model/sidebar/sba7aLoanConventionalTransactionFee.mdx" />
                }
              />
            }
          />
        </>
      )}

      <DealFees />

      {errorMessage && (
        <Alert variant="light" color="red" ta={"center"}>
          {errorMessage}
        </Alert>
      )}
    </Stack>
  );
};

export const ModuleSBA7aLoan = {
  key: "SBA 7(a) Loan",
  icon: <TbBuildingBank />,
  control: "SBA 7(a) Loan",
  panel: <Panel />,
  errorAtom: sbaLoanIsInErrorAtom,
};
